import { MessageMultiple } from "@/components/icons/customIcons.tsx";
import { OkThxWordmark } from "@/components/icons/Logo.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import { cn } from "@/lib/utils.ts";
import {
  NEW_CHAT_ROW,
  useNavRows,
  type NavIcon,
  type Pivot,
} from "@/components/layout/SidebarNavList.tsx";
import { PIVOT_LABELS } from "@/components/layout/pivotRoutes.ts";
import { SETTINGS_TAB_ICONS, settingsTabPath } from "@/views/Settings/settingsNav.ts";
import { newChatSignalAtom } from "@/components/keyboard/navSignals.ts";
import { useEmbedded } from "@/hooks/useEmbedded.tsx";
import { useSetAtom } from "jotai";
import { useRef } from "react";
import { useLocation } from "wouter";
import { Settings } from "lucide-react";

/**
 * Where mobile is: the thread list it opens on, or one of the pivots the URL
 * names. Desktop keeps the list in its sidebar and so has no pivot for it;
 * mobile has one column, so there the list is a destination like any other.
 */
export type MobileDestination = "threads" | Pivot;

/** What every row in the list carries, whether it names a pivot or the thread list. */
type NavRow = { value: MobileDestination; label: string; icon: NavIcon };

/**
 * The composer's own glyph, taken from the shared row rather than from lucide
 * directly: that descriptor is exported so this menu names New chat with the
 * same word and the same picture as the sidebar, and re-importing the icon is
 * how the two drift apart.
 */
const NewChatIcon = NEW_CHAT_ROW.icon;

/** Mobile's own row, since the thread list is a destination only here. */
const THREADS_ROW: NavRow = {
  value: "threads",
  label: "Threads",
  icon: MessageMultiple,
};

/**
 * The shape every control on the menu's footer row shares, so the three read as
 * one row of controls rather than as more list rows that happen to be styled.
 *
 * `focus:` is restated on each fill below because `DropdownMenuItem` tints
 * itself on highlight, and over a filled control that tint lands as a wash that
 * loses the colour.
 */
const FOOTER_ACTION_CLASS = "h-9 rounded-full";

/**
 * The two utilities: square, and filled with the very colour the menu marks its
 * current row in.
 *
 * `bg-background` rather than the `bg-secondary` a menu item highlights with.
 * The two sit a fraction apart in lightness but come from different families —
 * ash against zinc, warm against cool — so side by side in one row they read as
 * two greys that nearly match rather than as one. Highlight deepens to the next
 * step of the same warm ramp instead of crossing to the other.
 */
const FOOTER_ICON_CLASS = "w-9 justify-center px-0 bg-background focus:bg-accent";

/** The one action, and the only thing on the row wearing the brand colour. */
const FOOTER_PRIMARY_CLASS =
  "px-3 bg-primary text-primary-foreground focus:bg-primary focus:text-primary-foreground";

/**
 * The trigger's bars, which become a cross while the menu is open: the outer two
 * drop away, and the line they leave behind turns into one stroke of the cross
 * while its twin sweeps out of it to make the other.
 *
 * Driven entirely by the `data-state` Radix already stamps on the trigger,
 * through the `group/button` its base class already carries — so the open state
 * lives in exactly one place (the menu) rather than being mirrored into React
 * state here just to dress an icon.
 *
 * Bars rather than the lucide glyph, because its three are one `<path>` and
 * cannot be moved independently. `h-px` matches the hairline the icon drew at
 * `strokeWidth={1}`, and `inset-x-[3px]` matches where its bars started and
 * ended in the same 24px box, so the resting glyph is the one that was there.
 *
 * Nothing travels: the outer bars leave from where they rest and the cross forms
 * on the centre line, so no bar is both moving and turning at once. That pairing
 * was what read as a hinge — translating and rotating together cancels one end's
 * journey against the other's, leaving the top bar's left end moving about 3px
 * where its right moved about 12.
 *
 * The middle bar is the hinge of the whole thing, and it never leaves: it is the
 * line you are left with and then one arm of the cross, which is what ties the
 * two halves of the gesture together. Delays swap with the state, so closing
 * plays back in reverse — the cross folds to a single line, and only then do the
 * other two return.
 */
const MENU_GLYPH = (
  <span aria-hidden="true" className="relative block size-6">
    {/* The two that simply go. They hold their resting offsets throughout; the
        fade is the whole of their part.

        Out quickly and back slowly, which is the duration each direction
        transitions *toward*: leaving, they are clearing the way for the cross
        and should be gone before it starts, while returning they are the last
        thing to happen and a 150ms snap read as them popping back rather than
        settling. */}
    <span className="absolute inset-x-[3px] top-1/2 h-px -translate-y-[5px] bg-current opacity-100 transition-opacity delay-200 duration-300 group-data-[state=open]/button:opacity-0 group-data-[state=open]/button:delay-0 group-data-[state=open]/button:duration-150 motion-reduce:transition-none" />
    <span className="absolute inset-x-[3px] top-1/2 h-px translate-y-[5px] bg-current opacity-100 transition-opacity delay-200 duration-300 group-data-[state=open]/button:opacity-0 group-data-[state=open]/button:delay-0 group-data-[state=open]/button:duration-150 motion-reduce:transition-none" />
    {/* The one that stays, and then turns — waiting out the others' exit first,
        so there is a beat of a single line before the cross begins. */}
    <span className="absolute inset-x-[3px] top-1/2 h-px origin-center bg-current transition-transform duration-200 group-data-[state=open]/button:rotate-45 group-data-[state=open]/button:delay-150 motion-reduce:transition-none" />
    {/* Its twin, arriving out of it: it starts flat and unseen on the same line,
        so it reads as the stroke splitting rather than as a fourth bar. */}
    <span className="absolute inset-x-[3px] top-1/2 h-px origin-center bg-current opacity-0 transition-[opacity,rotate] duration-200 group-data-[state=open]/button:-rotate-45 group-data-[state=open]/button:opacity-100 group-data-[state=open]/button:delay-150 motion-reduce:transition-none" />
  </span>
);

/**
 * Mobile's way between the destinations desktop lists down its sidebar. One
 * button, sized to the bottom row's other control so the pair keeps its
 * proportions beside the composer.
 *
 * Every row selects a pivot rather than navigating somewhere: mobile renders the
 * same views desktop does, in its one column, so these are one screen showing
 * something different rather than seven pages to travel to.
 *
 * The wordmark leads, then the destinations, then a footer row of Settings,
 * Search and New chat below a rule. Threads takes Search's seat in the list,
 * since the thread list is a destination only here.
 */
export const MobileNavMenu = ({
  active,
  onSelect,
  className,
}: {
  /**
   * Which row to mark as current, or null from a screen that is not one of
   * these destinations at all — Settings, which the footer row reaches.
   */
  active: MobileDestination | null;
  onSelect: (destination: MobileDestination) => void;
  className?: string;
}) => {
  // Desktop's own list, in its order and behind its gates. Two copies would
  // drift the moment either side is reordered — and when the gate lived here
  // alone, they did: this menu hid Tasks while the sidebar showed it to
  // everyone.
  //
  // Threads takes the seat Search held, since desktop keeps the thread list *as*
  // its sidebar rather than as a place to go. Matched by name rather than by
  // index, so it keeps that seat if the shared order changes.
  //
  // Neither New chat nor Search is in this list, though desktop lists both: they
  // are on the footer row below. Naming either in both places would make it the
  // only destination said twice in one menu.
  const rows = useNavRows();
  const items: NavRow[] = rows.map((row) => (row.value === "library" ? THREADS_ROW : row));
  const requestNewChat = useSetAtom(newChatSignalAtom);
  // Settings is the one item here that leaves for a route of its own, and an
  // embedded surface suppresses the chrome header that screen relies on for its
  // way back — so offering it there strands the reader on a page with no exit.
  const { isWebView } = useEmbedded();
  // Whether this close is a departure or a dismissal. Radix parks focus back on
  // the trigger when the menu closes, which runs *after* the new screen has
  // mounted — so Search, which lands with its field focused, had the cursor
  // pulled straight back out to this button. A screen the user chose owns its
  // own focus; Escape or a tap outside changed nothing, so there the trigger is
  // still where they were.
  const departing = useRef(false);
  const [, setLocation] = useLocation();
  // A departure like any row above, so the menu hands focus to the screen it
  // opened rather than snapping it back to the trigger.
  const handleOpenSettings = () => {
    departing.current = true;
    setLocation(settingsTabPath("account"));
  };
  const handleSelect = (destination: MobileDestination) => {
    departing.current = true;
    // New chat goes through the signal every other new-chat trigger raises, so a
    // screen already showing it starts over with an empty field rather than
    // sitting on what was typed.
    //
    // The host is told either way. Only the shell listens for that signal, so a
    // menu opened from a screen outside it — Settings, which the footer row
    // reaches — would otherwise have one row that did nothing. The shell, which
    // hears both, leaves the move to the signal.
    if (destination === "newchat") requestNewChat((n) => n + 1);
    onSelect(destination);
  };

  return (
    // Not modal: a modal menu traps focus inside itself until its close
    // animation finishes unmounting it, and the screen behind it has already
    // changed by then. Search focusing its field mid-close was pulled straight
    // back into the trap, and unmounting then dropped focus on the body. This is
    // a nav menu over a screen it is about to replace, not a dialog needing the
    // page held still.
    <DropdownMenu
      modal={false}
      // Opening resets the flag. It is only cleared in `onCloseAutoFocus`, and a
      // close that skips that handler — the chosen destination swapping the
      // screen out from under a non-modal menu — would leave it set, so the next
      // Escape would decline to return focus and drop it on the body instead.
      onOpenChange={(open) => {
        if (open) departing.current = false;
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          // The pill this replaces was `size-14` of card with a 24px glyph;
          // matching it keeps the row's rhythm against the composer.
          //
          // `shadow-2xl` is the composer's own, so the two sit at one elevation
          // on the rows where they share the band and this still reads as lifted
          // off the page on the New chat screen where it stands alone.
          //
          // Elevation only, not the composer's outline with it: that edge is
          // what tells a field where its typing area begins, and a button has no
          // such boundary to draw. `Button`'s base already sets
          // `border-transparent`, so leaving it alone is the whole of it.
          className={cn(
            "size-14 shrink-0 rounded-full bg-card shadow-2xl hover:bg-card",
            className,
          )}
          aria-label="Menu"
          data-statsig-name="mobile-nav-menu"
        >
          {MENU_GLYPH}
        </Button>
      </DropdownMenuTrigger>
      {/* Opens upward: the trigger sits on the bottom edge of the screen.
          Wider than the rows need, for the footer: the list sets no lower bound
          on the sheet's width, and the two utilities and the action have to read
          as two groups at opposite ends of a row rather than one cluster. */}
      <DropdownMenuContent
        align="start"
        side="top"
        className="w-64"
        onCloseAutoFocus={(event) => {
          if (!departing.current) return;
          departing.current = false;
          event.preventDefault();
        }}
      >
        {/* The same mark the app's other menu wears at its head, at the same
            scale, so the two read as one piece of chrome rather than as two
            menus that happen to share a screen. It goes home like a wordmark
            should — which here is the thread list, the screen mobile opens on
            and the one Threads below selects. */}
        <DropdownMenuItem
          className="px-2 pt-3 pb-2.5 focus:bg-transparent"
          onSelect={() => handleSelect("threads")}
          data-statsig-name="mobile-nav-wordmark"
          aria-label="Threads"
        >
          <OkThxWordmark className="scale-75 origin-left" />
        </DropdownMenuItem>
        {items.map(({ value, label, icon: Icon }) => (
          <DropdownMenuItem
            key={value}
            className={cn("py-2.5", active === value && "bg-background")}
            onSelect={() => handleSelect(value)}
            data-statsig-name={`mobile-nav-${value}`}
          >
            <Icon className="size-4" /> {label}
          </DropdownMenuItem>
        ))}
        {/* Below a rule, because Settings leaves for a screen of its own and New
            chat starts something, rather than swapping what the one screen is
            showing the way the rows above do. Search is a destination like those
            rows and sits here for the row's shape, not for its behaviour.

            Still `DropdownMenuItem`s inside the row rather than bare buttons.
            Radix registers items through context rather than by walking the DOM,
            so a wrapper keeps them in the roving order — every one of them still
            carries `data-radix-collection-item`, and arrow keys still reach them.

            `p-1` all round rather than only above: with the menu's own 4px and
            its border, that puts the same 9px under the row that sits either
            side of it, so it is inset from the sheet evenly instead of resting
            on its bottom edge. */}
        <DropdownMenuSeparator />
        <DropdownMenuGroup className="flex items-center justify-between gap-2 p-1">
          {/* The two utilities, paired at the leading edge and left neutral:
              one action is the point of this row, and three filled in the brand
              colour would make none of them it. */}
          <div className="flex items-center gap-1">
            {isWebView ? null : (
              <DropdownMenuItem
                className={cn(FOOTER_ACTION_CLASS, FOOTER_ICON_CLASS)}
                onSelect={handleOpenSettings}
                aria-label="Settings"
                data-statsig-name="mobile-nav-settings"
              >
                <Settings className="size-4" />
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              className={cn(FOOTER_ACTION_CLASS, FOOTER_ICON_CLASS)}
              onSelect={() => handleSelect("library")}
              aria-label={PIVOT_LABELS.library}
              data-statsig-name="mobile-nav-library"
            >
              <SETTINGS_TAB_ICONS.library className="size-4" />
            </DropdownMenuItem>
          </div>
          <DropdownMenuItem
            className={cn(FOOTER_ACTION_CLASS, FOOTER_PRIMARY_CLASS)}
            onSelect={() => handleSelect("newchat")}
            data-statsig-name="mobile-nav-newchat-action"
          >
            <NewChatIcon className="size-4" />
            {NEW_CHAT_ROW.label}
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
